/**
 * Swimming: a body in the water, posed after its animation each frame (like the lean in a kiss). Moving, it lies
 * almost flat and swims freestyle: each hand round a circle from its shoulder, half a stroke apart (pull under,
 * recover over), elbows high, a flutter kick behind; the stroke quickens with speed. Still, it treads water:
 * upright, the hands sculling in front of the chest, a slow kick. (The reference: Taipei's swim state.)
 *
 * Two-bone IK puts the hands and feet where the stroke wants them, so it works on either rig.
 */
import * as THREE from 'three';

export interface Rig { arm: [string, string, string][]; leg: [string, string, string][] }
export const RIGS: Rig[] = [
  { arm: [['upperarm01_L', 'lowerarm01_L', 'wrist_L'], ['upperarm01_R', 'lowerarm01_R', 'wrist_R']], leg: [['upperleg01_L', 'lowerleg01_L', 'foot_L'], ['upperleg01_R', 'lowerleg01_R', 'foot_R']] },
  { arm: [['Bip01_L_UpperArm', 'Bip01_L_Forearm', 'Bip01_L_Hand'], ['Bip01_R_UpperArm', 'Bip01_R_Forearm', 'Bip01_R_Hand']], leg: [['Bip01_L_Thigh', 'Bip01_L_Calf', 'Bip01_L_Foot'], ['Bip01_R_Thigh', 'Bip01_R_Calf', 'Bip01_R_Foot']] },
];

export class SwimPose {
  private bones = new Map<string, THREE.Bone>();
  private rig: Rig | null = null;
  /** the top of the skeleton (the hips): the whole body pitches about it */
  private top: THREE.Bone | null = null;
  private phase = 0;
  private time = 0;
  /** 0 treading .. 1 swimming, eased */
  private stroke = 0;
  /** how far the body is laid down now (eased) */
  private pitch = 0;

  /** the arm bones' rest rotations (the bind pose: arms out to the side) */
  private rest = new Map<string, THREE.Quaternion>();
  /** each upper arm's collarbone (reset with it) */
  private collar = new Map<string, THREE.Bone>();
  /** each arm's stroke rotation last frame (to keep the blend on the same side) */
  private lastArm: (THREE.Quaternion | null)[] = [null, null];
  private lastTread: (THREE.Quaternion | null)[] = [null, null];
  private swimming = false;

  constructor(private root: THREE.Object3D) {
    root.traverse((o) => { if ((o as THREE.Bone).isBone) this.bones.set(o.name, o as THREE.Bone); });
    // the rest pose, read off the skeleton's bind pose (the animation puts its own back on the next frame)
    let sk: THREE.Skeleton | null = null;
    root.traverse((o) => { if (!sk && (o as THREE.SkinnedMesh).isSkinnedMesh) sk = (o as THREE.SkinnedMesh).skeleton; });
    if (sk) {
      const keep = new Map([...this.bones].map(([n, b]) => [n, b.quaternion.clone()]));
      (sk as THREE.Skeleton).pose();
      for (const arm of RIGS.flatMap((r) => r.arm)) {
        for (const n of arm) { const b = this.bones.get(n); if (b) this.rest.set(n, b.quaternion.clone()); } // (the wrist too: the clip under the swim left the hand bent)
        // (and the collarbone above it: the animation underneath pops it once a loop, and the arm went with it)
        const up = this.bones.get(arm[0])?.parent as THREE.Bone | undefined;
        if (up?.isBone && /clav|shoulder/i.test(up.name)) { this.rest.set(up.name, up.quaternion.clone()); this.collar.set(arm[0], up); }
      }
      for (const [n, q] of keep) this.bones.get(n)!.quaternion.copy(q);
    }
    this.rig = RIGS.find((r) => r.arm.flat().every((n) => this.bones.has(n))) ?? null;
    root.traverse((o) => { if (!this.top && (o as THREE.Bone).isBone && !(o.parent as THREE.Bone | null)?.isBone) this.top = o as THREE.Bone; });
  }

  get ok() { return !!this.rig && !!this.top; }



  /** after the animation: `speed` 0..1 (how hard you're moving); `base`: the height the water holds you at here */
  apply(dt: number, speed: number, base: number) {
    const rig = this.rig, top = this.top;
    if (!rig || !top) return;
    this.time += dt;
    // (swimming starts above 0.15 and stops under 0.05: one threshold, and a speed hovering at it kept the body half
    // treading, half swimming)
    if (speed > 0.15) this.swimming = true; else if (speed < 0.05) this.swimming = false;
    this.stroke = THREE.MathUtils.damp(this.stroke, this.swimming ? 1 : 0, 3, dt);
    const k = this.stroke;
    this.phase = (this.phase + dt * (0.35 + k * 0.55)) % 1;
    // lay the body down: pitch the skeleton forward about the hips, round the body's own left-right axis
    this.pitch = THREE.MathUtils.damp(this.pitch, 0.18 + k * 1.25, 4, dt);
    // at the surface: swimming, your back at the waterline and your head just out; treading, the shoulders at it
    // (the ground under you holds your feet at swimming depth: this lifts the body from there, each frame)
    this.root.position.y = base - 0.22 + 0.58 * k;
    this.root.updateMatrixWorld(true);
    const fwd = new THREE.Vector3(0, 0, 1).transformDirection(this.root.matrixWorld).setY(0).normalize();
    const right = new THREE.Vector3(0, 1, 0).cross(fwd).normalize(); // (up × facing: the body's LEFT side; the pitch turns about it)
    const tilt = new THREE.Quaternion().setFromAxisAngle(right, this.pitch);
    turn(top, tilt);
    // the body's axis now: from the hips towards the head; "up" off the back
    const hips = top.getWorldPosition(new THREE.Vector3());
    // ("up off the back": the back of an upright body, tilted with it. It used to be flipped whenever it pointed down,
    // and lying nearly flat it sits right at level: it flipped back and forth, and the arms swung round with it)
    const axis = new THREE.Vector3(0, 1, 0).applyQuaternion(tilt).normalize(), back = fwd.clone().negate().applyQuaternion(tilt).normalize();
    // the arms: each turns from its shoulder like a windmill, nearly straight, half a turn apart (freestyle): reach
    // ahead, pull down under the body to the hip, then swing back over the water (a little bend at the elbow on the way
    // over); treading, they're out to the sides, sculling from the shoulder. No hand-chasing IK: that folded the elbows.
    rig.arm.forEach((arm, i) => {
      const out = i ? -1 : 1; // (the arm's own side: L is along `right`, R against it)
      { const c = this.collar.get(arm[0]), q = c && this.rest.get(c.name); if (c && q) { c.quaternion.copy(q); c.updateMatrixWorld(true); } }
      for (const n of arm) { const q = this.rest.get(n), b = this.bones.get(n); if (q && b) { b.quaternion.copy(q); b.updateMatrixWorld(true); } }
      const A = this.bones.get(arm[0])!, B = this.bones.get(arm[1])!, C = this.bones.get(arm[2])!;
      const sh = A.getWorldPosition(new THREE.Vector3()), el = B.getWorldPosition(new THREE.Vector3()), ha = C.getWorldPosition(new THREE.Vector3());
      const la = sh.distanceTo(el), lb = el.distanceTo(ha);
      const th = (this.phase + i * 0.5) * Math.PI * 2, over = Math.sin(th) < 0; // (second half of the turn: the recovery, over the water)
      // swimming: the arm points along the body's axis (a touch out from the side) and turns round the side-to-side line
      // by the stroke's angle: an explicit turn, so it never snaps (the shortest way from "ahead" to "at the hip" has
      // no single answer: that flipped the arm half a turn)
      const reachDir = axis.clone().addScaledVector(right, out * 0.18).normalize();
      const spin = new THREE.Vector3().crossVectors(axis, back).normalize(); // (turning about this by −θ takes `axis` towards −back first: the pull)
      // (forwards freestyle: ahead → down through the water → the hip → up and over → ahead. Turned the other way it was
      // a backward windmill: the hands came out at the front and pulled back through the air)
      const qStroke = new THREE.Quaternion().setFromAxisAngle(spin, -th);
      const swim = reachDir.clone().applyQuaternion(qStroke);
      // treading: out to the side and a little forward, sweeping in and out
      const sc = Math.sin(this.time * 2.6 + i * Math.PI);
      const tread = right.clone().multiplyScalar(out * 0.75).addScaledVector(fwd, 0.45 + sc * 0.25).add(new THREE.Vector3(0, -0.35, 0)).normalize();
      // the upper arm, turned from its rest pose: to the reach, then round the stroke (treading: blended towards the side)
      const rest0 = el.clone().sub(sh).normalize();
      const qReach = new THREE.Quaternion().setFromUnitVectors(rest0, reachDir);
      const qTread = new THREE.Quaternion().setFromUnitVectors(rest0, tread);
      // (blended by hand, keeping each side the way it was last frame: slerp takes the shorter way round, and once a stroke
      // the shorter way switched sides mid-blend: the arm snapped half a turn and back)
      const qT = qStroke.clone().multiply(qReach), last = this.lastArm[i];
      if (last && qT.dot(last) < 0) qT.set(-qT.x, -qT.y, -qT.z, -qT.w);
      this.lastArm[i] = qT.clone();
      const lt = this.lastTread[i];
      if (lt && qTread.dot(lt) < 0) qTread.set(-qTread.x, -qTread.y, -qTread.z, -qTread.w);
      this.lastTread[i] = qTread.clone();
      const qArm = new THREE.Quaternion(qTread.x * (1 - k) + qT.x * k, qTread.y * (1 - k) + qT.y * k, qTread.z * (1 - k) + qT.z * k, qTread.w * (1 - k) + qT.w * k).normalize();
      turn(A, qArm);
      // the forearm: straight through the stroke, so the whole arm sweeps round from the shoulder (a bent elbow read as
      // flapping); a soft bend only when treading water
      const bend = (over ? 0.06 : 0.03) * k + 0.2 * (1 - k);
      B.updateMatrixWorld(true);
      turn(B, new THREE.Quaternion().setFromAxisAngle(spin.clone().applyQuaternion(qStroke).normalize(), -bend * out * 0 - bend));
      void swim; void la; void lb; void ha;
    });
    // the legs: straight back from the hips, a flutter kick (fast, small); treading: a slow wide kick below
    rig.leg.forEach((leg, i) => {
      const hip = this.bones.get(leg[0])!.getWorldPosition(new THREE.Vector3());
      const kick = Math.sin(this.time * (5 + k * 6) + i * Math.PI);
      const swim = hip.clone().addScaledVector(axis, -0.85).addScaledVector(back, kick * 0.14);
      const tread = hip.clone().add(new THREE.Vector3(0, -0.8, 0)).addScaledVector(fwd, kick * 0.15).addScaledVector(right, (i ? -1 : 1) * 0.12);
      const pole = hip.clone().addScaledVector(back, -1).addScaledVector(fwd, 0.4 * (1 - k)); // (knees down)
      reach(this.bones, leg, tread.lerp(swim, k), pole);
    });
    void hips;
  }
}

const _s = new THREE.Vector3(), _e = new THREE.Vector3(), _h = new THREE.Vector3(), _d = new THREE.Vector3(), _p = new THREE.Vector3(), _w = new THREE.Vector3();
const _u = new THREE.Vector3(), _v = new THREE.Vector3(), _q = new THREE.Quaternion(), _qb = new THREE.Quaternion(), _qp = new THREE.Quaternion();

/** turn a bone by a world-space rotation (about its own origin) */
export function turn(bone: THREE.Bone, q: THREE.Quaternion) {
  bone.getWorldQuaternion(_qb);
  (bone.parent ?? bone).getWorldQuaternion(_qp);
  if (!bone.parent) _qp.identity();
  bone.quaternion.copy(_qp.invert().multiply(q.clone().multiply(_qb)));
  bone.updateMatrixWorld(true);
}

/** Two-bone IK: bend at the middle joint so the end lands on the target, the bend towards the pole (as sitter.ts). */
export function reach(bones: Map<string, THREE.Bone>, [a, b, c]: [string, string, string], target: THREE.Vector3, pole: THREE.Vector3) {
  const A = bones.get(a), B = bones.get(b), C = bones.get(c);
  if (!A || !B || !C) return;
  const s = A.getWorldPosition(_s), e = B.getWorldPosition(_e), h = C.getWorldPosition(_h);
  const la = s.distanceTo(e), lb = e.distanceTo(h);
  const toT = _d.subVectors(target, s);
  const d = THREE.MathUtils.clamp(toT.length(), Math.abs(la - lb) + 1e-3, la + lb - 1e-3);
  toT.normalize();
  const x = (la * la - lb * lb + d * d) / (2 * d), y = Math.sqrt(Math.max(0, la * la - x * x));
  const bend = _p.subVectors(pole, s);
  bend.addScaledVector(toT, -bend.dot(toT)).normalize();
  const elbow = _w.copy(s).addScaledVector(toT, x).addScaledVector(bend, y);
  aim(A, e, elbow);
  B.updateMatrixWorld(true);
  aim(B, C.getWorldPosition(_h), _w.copy(s).addScaledVector(toT, d));
}

function aim(bone: THREE.Bone, from: THREE.Vector3, to: THREE.Vector3) {
  const o = bone.getWorldPosition(_v);
  _u.subVectors(from, o).normalize();
  const want = new THREE.Vector3().subVectors(to, o).normalize();
  _q.setFromUnitVectors(_u, want);
  bone.getWorldQuaternion(_qb);
  bone.parent!.getWorldQuaternion(_qp);
  bone.quaternion.copy(_qp.invert().multiply(_q.multiply(_qb)));
  bone.updateMatrixWorld(true);
}
